import { ReactNode, useState, useEffect, useRef } from 'react'
import { useGameStore } from '@store/gameStore'
import { DailyReportModal } from './DailyReportModal'
import { getVIPIcon } from '@utils/vipHelpers'
import './GameLayout.css'

interface GameLayoutProps {
    children: ReactNode
    showNav?: boolean
    showHeader?: boolean
    noPadding?: boolean
    noScroll?: boolean
}

export function GameLayout({
    children,
    showNav = true,
    showHeader = true,
    noPadding = false,
    noScroll = false,
}: GameLayoutProps) {
    const resources = useGameStore(state => state.resources)
    const power = useGameStore(state => state.power)
    const currentScreen = useGameStore(state => state.currentScreen)
    const setScreen = useGameStore(state => state.setScreen)
    const isVIP = useGameStore(state => state.isVIP)
    const vipLevel = useGameStore(state => state.vipLevel)
    const equipment = useGameStore(state => state.equipment) || {}
    const heroSkin = useGameStore(state => state.heroSkin)
    const empireColor = useGameStore(state => state.empireColor)
    const loan = useGameStore(state => state.loan)
    const processLoanPayment = useGameStore(state => state.processLoanPayment)
    const statPoints = useGameStore(state => state.statPoints)
    const tutorialPhase = useGameStore(state => state.tutorialPhase)
    const tutorialSubStep = useGameStore(state => state.tutorialSubStep)
    const setTutorialPhase = useGameStore(state => state.setTutorialPhase)
    const setTutorialSubStep = useGameStore(state => state.setTutorialSubStep)
    const resetGame = useGameStore(state => state.resetGame)

    const [isResetConfirmOpen, setIsResetConfirmOpen] = useState(false)

    // Daily Report Modal State
    const showDailyReport = useGameStore(state => (state as any).showDailyReport || false)
    const dailyReportData = useGameStore(state => (state as any).dailyReportData || null)

    const totalGameMinutes = useGameStore(state => state.totalGameMinutes)

    const gameDay = Math.floor(totalGameMinutes / (24 * 60)) + 1
    const gameHour = Math.floor((totalGameMinutes % (24 * 60)) / 60)
    const gameMin = totalGameMinutes % 60

    // Calculate total power including equipment and stats
    const stats = useGameStore(state => state.stats)
    const equipmentPower = Object.values(equipment).reduce(
        (sum, item) => sum + (item?.power || 0),
        0
    )
    const statPower = (stats.atk + stats.def + stats.spd) * 5
    const totalPower = power + equipmentPower + statPower

    // Hero Image Map
    const HERO_IMAGES: { [key: string]: string } = {
        'default': '/rookie.png',
        'knight': '/ritter.png',
        'king': '/king.png',
        'wizard': '/magier.png'
    }

    // Track gem spending for animation
    const [gemSpendAmount, setGemSpendAmount] = useState<number | null>(null)
    const prevGemsRef = useRef(resources.gems)

    // Track euro payment animation
    const [euroPaymentAmount, setEuroPaymentAmount] = useState<number | null>(null)
    const prevEuroRef = useRef(resources.euro ?? 1000)

    useEffect(() => {
        const prevGems = prevGemsRef.current
        const currentGems = resources.gems

        // Detect gem spending (not gaining)
        if (currentGems < prevGems) {
            const spent = prevGems - currentGems
            setGemSpendAmount(spent)

            // Clear after animation
            setTimeout(() => setGemSpendAmount(null), 1500)
        }

        prevGemsRef.current = currentGems
    }, [resources.gems])

    // Detect euro reduction (loan payment)
    useEffect(() => {
        const prevEuro = prevEuroRef.current
        const currentEuro = resources.euro ?? 1000

        if (currentEuro < prevEuro) {
            const paid = prevEuro - currentEuro
            setEuroPaymentAmount(paid)

            // Clear after animation
            setTimeout(() => setEuroPaymentAmount(null), 1500)
        }

        prevEuroRef.current = currentEuro
    }, [resources.euro])

    // LOAN TICKER - Every 60 seconds
    useEffect(() => {
        if (loan <= 0) return

        const interval = setInterval(() => {
            processLoanPayment()
        }, 60000) // Every 60 seconds

        return () => clearInterval(interval)
    }, [loan, processLoanPayment])

    return (
        <div className="game-layout">
            {/* Subheader - Time & Bank */}
            {showHeader && (
                <div className="game-subheader">
                    <div className="game-subheader__time">
                        🕒 Tag {gameDay}, {gameHour.toString().padStart(2, '0')}:{gameMin.toString().padStart(2, '0')}
                        <span className="game-subheader__restart" onClick={() => setIsResetConfirmOpen(true)} title="Spiel zurücksetzen">🔄</span>

                    </div>

                    <div className="game-subheader__logo">
                        <img src="/logo-breit.png" alt="PAY2LOSE" className="game-subheader__logo-img" />
                    </div>

                    <div className="game-subheader__bank">
                        <div
                            id="nav-bank-btn"
                            className="game-subheader__bank-btn"
                            onClick={() => {
                                setScreen('bank')
                                if (tutorialPhase === 'bank' && tutorialSubStep === 0) {
                                    setTutorialSubStep(1)
                                }
                            }}
                        >
                            <span className="game-subheader__card-icon">💳</span>
                            <span className="game-subheader__balance">
                                {(resources.euro ?? 0).toFixed(2)}€
                            </span>
                        </div>

                        {/* Euro Payment Animation moved here */}
                        {euroPaymentAmount !== null && (
                            <span className="game-header__euro-anim">-{euroPaymentAmount.toFixed(2)}€</span>
                        )}
                    </div>
                </div>
            )}

            {/* Header - Resource Bar */}
            {showHeader && (
                <header className="game-header">
                    {/* Left: Player Info */}
                    <div className="game-header__player" onClick={() => setScreen('hero')}>
                        <div className="game-header__avatar" style={{ borderLeftColor: empireColor || '#3b82f6' }}>
                            <img src={HERO_IMAGES[heroSkin] || HERO_IMAGES.default} alt="Avatar" className="game-header__avatar-img" />
                        </div>
                        <div className="game-header__power">
                            <span className="game-header__power-icon">⚔️</span>
                            <span className="game-header__power-value">{totalPower.toLocaleString()}</span>
                        </div>
                    </div>

                    {/* Center/Right: Resources */}
                    <div className="game-header__resources">
                        {/* Energy */}
                        <div className="game-header__resource" onClick={() => { useGameStore.getState().setShopTab('packs'); setScreen('shop') }}>
                            <span className="game-header__resource-icon">⚡</span>
                            <span className="game-header__resource-value">{resources.energy}</span>
                        </div>

                        {/* Gold (In-Game Currency) */}
                        <div className="game-header__resource" onClick={() => setScreen('city')}>
                            <span className="game-header__resource-icon">🪙</span>
                            <span className="game-header__resource-value">{resources.gold >= 1000 ? `${(resources.gold / 1000).toFixed(1)}K` : Math.floor(resources.gold)}</span>
                        </div>

                        {/* Gems */}
                        <div className="game-header__resource game-header__resource--gems" onClick={() => { useGameStore.getState().setShopTab('packs'); setScreen('shop') }}>
                            <span className="game-header__resource-icon">💎</span>
                            <span className="game-header__resource-value">{resources.gems >= 1000 ? `${(resources.gems / 1000).toFixed(1)}K` : resources.gems}</span>

                            {/* Spend Animation */}
                            {gemSpendAmount !== null && (
                                <span className="game-header__spend-anim">-{gemSpendAmount.toFixed(0)}</span>
                            )}
                        </div>
                    </div>

                    {/* VIP Badge */}
                    {isVIP && (
                        <div className="game-header__vip">{getVIPIcon(vipLevel, isVIP)}</div>
                    )}
                </header>
            )}

            {/* Content */}
            <main className={`game-layout__content ${noPadding ? 'game-layout__content--no-padding' : ''} ${noScroll ? 'game-layout__content--no-scroll' : ''}`}>
                {children}
            </main>

            {/* Bottom Navigation - 5 Core Tabs */}
            {showNav && (
                <nav className="game-nav">
                    <button
                        id="nav-city-btn"
                        className={`game-nav__btn ${currentScreen === 'city' ? 'game-nav__btn--active' : ''}`}
                        onClick={() => {
                            setScreen('city')
                            if (tutorialPhase === 'daily' && tutorialSubStep === 2) {
                                setTutorialPhase('bank' as any) // Phase will advance to bank sub 0
                                setTutorialSubStep(0)
                            }
                        }}
                    >
                        <span className="game-nav__icon">🏠</span>
                        <span className="game-nav__label">Stadt</span>
                    </button>

                    <button
                        className={`game-nav__btn ${currentScreen === 'hero' ? 'game-nav__btn--active' : ''}`}
                        onClick={() => setScreen('hero')}
                    >
                        <span className="game-nav__icon">
                            🦸
                            {statPoints > 0 && (
                                <span className="game-nav__badge">{statPoints}</span>
                            )}
                        </span>
                        <span className="game-nav__label">Held</span>
                    </button>

                    <button
                        className={`game-nav__btn ${currentScreen === 'shop' ? 'game-nav__btn--active' : ''}`}
                        onClick={() => setScreen('shop')}
                    >
                        <span className="game-nav__icon">🛒</span>
                        <span className="game-nav__label">Shop</span>
                    </button>

                    <button
                        className={`game-nav__btn ${currentScreen === 'battle' ? 'game-nav__btn--active' : ''}`}
                        onClick={() => setScreen('battle')}
                    >
                        <span className="game-nav__icon">⚔️</span>
                        <span className="game-nav__label">Kampf</span>
                    </button>

                    <button
                        className={`game-nav__btn ${currentScreen === 'ranking' ? 'game-nav__btn--active' : ''}`}
                        onClick={() => setScreen('ranking')}
                    >
                        <span className="game-nav__icon">🏆</span>
                        <span className="game-nav__label">PvP</span>
                    </button>
                </nav>
            )}

            {/* Daily Report Modal - Global Overlay */}
            {showDailyReport && dailyReportData && (
                <DailyReportModal
                    isOpen={showDailyReport}
                    data={dailyReportData}
                    dayNumber={gameDay - 1}
                    debt={loan}
                    onClose={() => useGameStore.setState({ showDailyReport: false } as any)}
                />
            )}

            {/* Reset Confirmation Modal */}
            {isResetConfirmOpen && (
                <div className="game-modal-overlay" onClick={() => setIsResetConfirmOpen(false)}>
                    <div className="game-reset-modal" onClick={e => e.stopPropagation()}>
                        <div className="game-reset-modal__icon">⚠️</div>
                        <h2 className="game-reset-modal__title">Bist du dir sicher?</h2>
                        <p className="game-reset-modal__text">
                            Dein gesamter Spielfortschritt, alle Gebäude, Items und dein Rang werden <strong>unwiderruflich gelöscht</strong>.
                        </p>
                        <p className="game-reset-modal__hint">Dieser Vorgang kann nicht rückgängig gemacht werden!</p>

                        <div className="game-reset-modal__actions">
                            <button
                                className="game-reset-modal__btn game-reset-modal__btn--danger"
                                onClick={() => {
                                    resetGame()
                                    setIsResetConfirmOpen(false)
                                }}
                            >
                                JA, ALLES LÖSCHEN 🔥
                            </button>
                            <button
                                className="game-reset-modal__btn game-reset-modal__btn--ghost"
                                onClick={() => setIsResetConfirmOpen(false)}
                            >
                                ABBRECHEN
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}
